import type { ConditionRule, RuleType, ConditionCategory } from '../types';
export { RULE_TYPE_LABELS } from '../types';

/**
 * 常量定义
 */

/** 刷新间隔（毫秒）— 交易时间 */
export const REFRESH_INTERVALS = {
  MARKET: 5000,        // 行情刷新 5s
  BREADTH: 3000,       // 大盘状态 3s
  PORTFOLIO: 5000,     // 持仓刷新 5s
  CANDIDATES: 10000,   // 候选股刷新 10s
  ACCOUNT: 10000,      // 账户刷新 10s
} as const;

/** 刷新间隔（毫秒）— 非交易时间（降低频率省资源） */
export const OFF_HOURS_INTERVALS = {
  MARKET: 30000,       // 30s
  BREADTH: 30000,      // 30s
  PORTFOLIO: 30000,    // 30s
  CANDIDATES: 60000,   // 60s
  ACCOUNT: 60000,      // 60s
} as const;

/** 判断当前是否为 A 股交易时间（北京时间） */
export function isTradingTime(date: Date = new Date()): boolean {
  // 浏览器本地时间 = 用户时区时间（中国用户 = 北京时间）
  const day = date.getDay();
  if (day < 1 || day > 5) return false; // 周末
  const minutes = date.getHours() * 60 + date.getMinutes();
  const morningStart = 9 * 60 + 30;
  const morningEnd = 11 * 60 + 30;
  const afternoonStart = 13 * 60;
  const afternoonEnd = 15 * 60;
  return (
    (minutes >= morningStart && minutes <= morningEnd) ||
    (minutes >= afternoonStart && minutes <= afternoonEnd)
  );
}

/** 根据交易时段获取刷新间隔 */
export function getRefreshInterval(key: keyof typeof REFRESH_INTERVALS): number {
  return isTradingTime() ? REFRESH_INTERVALS[key] : OFF_HOURS_INTERVALS[key];
}

/** 分页默认值 */
export const DEFAULT_PAGE_SIZE = 50;

/** 虚拟资金默认买入股数 */
export const DEFAULT_BUY_QUANTITY = 1000;

/** 规则类型选项（用于编辑器下拉框） */
export const RULE_TYPE_OPTIONS: { value: RuleType; label: string; category: ConditionCategory[] }[] = [
  { value: 'turnover_rate_consecutive', label: '连续N日换手率', category: ['buy'] },
  { value: 'price_from_low', label: '距最低价涨幅', category: ['buy'] },
  { value: 'price_amplitude', label: 'N日振幅', category: ['buy'] },
  { value: 'exclude_st', label: '排除ST股', category: ['buy'] },
  { value: 'limit_up_history', label: '涨停历史', category: ['buy'] },
  { value: 'listing_days', label: '上市天数', category: ['buy'] },
  { value: 'profit_threshold', label: '盈利止盈', category: ['sell'] },
  { value: 'loss_threshold', label: '亏损止损', category: ['sell'] },
  { value: 'market_breadth_pause', label: '涨跌家数暂停', category: ['global'] },
  { value: 'market_index_pause', label: '指数涨跌暂停', category: ['global'] },
  { value: 'held_filter', label: '已持仓过滤', category: ['buy'] },
  { value: 'sell_cooldown', label: '止盈卖出冷却', category: ['buy'] },
  { value: 'loss_double', label: '止损加仓倍数', category: ['buy'] },
  { value: 'consecutive_loss_double', label: '连续亏损翻倍', category: ['buy'] },
  { value: 'max_price', label: '单价上限', category: ['buy'] },
  { value: 'same_day_sell_block', label: '当日禁止卖出', category: ['global'] },
  { value: 'daily_max_buys', label: '每日最多买入', category: ['buy'] },
  { value: 'buy_sort_by', label: '撞顶时优先选', category: ['buy'] },
  { value: 'screen_low_30d_max_pct', label: '收盘价≤近30日最低点+%', category: ['buy'] },
  { value: 'screen_up_days', label: '近N日至少M天上涨', category: ['buy'] },
  { value: 'loss_30d_low_stop_days', label: '近N日最低价止损', category: ['sell'] },
  { value: 'low_turnover_sell', label: '连续低换手卖出', category: ['sell'] },
  { value: 'volume_ratio_consecutive', label: '量比连续N日≥阈值', category: ['buy'] },
  { value: 'float_market_cap_max', label: '流通市值上限', category: ['buy'] },
  { value: 'open_jump_cancel', label: '开盘涨幅过高取消买入', category: ['buy'] },
  { value: 'max_portfolio_value', label: '持仓总市值上限', category: ['buy'] },
  { value: 'avg_down_buy', label: '补仓买入', category: ['buy'] },
  { value: 'avg_down_sell', label: '补仓后上涨卖出', category: ['sell'] },
];

/** 运算符选项 */
export const OPERATOR_OPTIONS = [
  { value: '>', label: '大于 >' },
  { value: '<', label: '小于 <' },
  { value: '>=', label: '大于等于 >=' },
  { value: '<=', label: '小于等于 <=' },
  { value: '==', label: '等于 ==' },
  { value: '!=', label: '不等于 !=' },
];

/** 条件分类标签 */
export const CATEGORY_LABELS: Record<ConditionCategory, string> = {
  buy: '入选条件',
  sell: '卖出条件',
  global: '整体条件',
};

/** 默认预设条件模板（用于恢复默认） */
export const DEFAULT_CONDITION_TEMPLATES: Omit<ConditionRule, 'id' | 'createdAt' | 'updatedAt'>[] = [
  // 入选条件
  {
    category: 'buy',
    ruleType: 'turnover_rate_consecutive',
    params: { consecutiveDays: 3, threshold: 7 },
    description: '连续3日换手率>7%',
    enabled: true,
    isPreset: true,
  },
  {
    category: 'buy',
    ruleType: 'volume_ratio_consecutive',
    params: { consecutiveDays: 3, lookback: 5, threshold: 3 },
    description: '连续3日量比≥3（当日成交÷5日均量）',
    enabled: true,
    isPreset: true,
  },
  {
    category: 'buy',
    ruleType: 'price_amplitude',
    operator: '<=',
    value: '5',
    params: { days: 20 },
    description: '近20个交易日振幅≤5%（最高点与最低点相差不超过5%）',
    enabled: true,
    isPreset: true,
  },
  {
    category: 'buy',
    ruleType: 'exclude_st',
    description: '排除ST及可能ST股',
    enabled: true,
    isPreset: true,
  },
  {
    category: 'buy',
    ruleType: 'limit_up_history',
    params: { months: 6, minCount: 1 },
    description: '半年内至少1次涨停封板',
    enabled: true,
    isPreset: true,
  },
  {
    category: 'buy',
    ruleType: 'listing_days',
    operator: '>=',
    value: '90',
    params: { minDays: 90 },
    description: '上市天数≥3个月(90个交易日)',
    enabled: true,
    isPreset: true,
  },
  {
    category: 'buy',
    ruleType: 'held_filter',
    description: '已持仓未卖出的股票不得入选',
    enabled: true,
    isPreset: true,
  },
  {
    category: 'buy',
    ruleType: 'sell_cooldown',
    operator: '<=',
    value: '30',
    params: { days: 30 },
    description: '止盈卖出后30个交易日内不得再次入选',
    enabled: true,
    isPreset: true,
  },
  {
    category: 'buy',
    ruleType: 'loss_double',
    operator: '==',
    value: '2',
    params: { multiplier: 2 },
    description: '止损卖出后再次入选按2倍资金买入',
    enabled: true,
    isPreset: true,
  },
  {
    category: 'buy',
    ruleType: 'consecutive_loss_double',
    operator: '==',
    value: '2',
    params: { factor: 2 },
    description: '连续亏损卖出后每次入选买入金额较上次翻倍',
    enabled: true,
    isPreset: true,
  },
  {
    category: 'buy',
    ruleType: 'max_price',
    operator: '<=',
    value: '100',
    params: { maxPrice: 100 },
    description: '单价不超过100元',
    enabled: true,
    isPreset: true,
  },
  {
    category: 'buy',
    ruleType: 'avg_down_buy',
    operator: '>=',
    value: '5',
    params: { pct: 5 },
    description: '持仓较买入价下跌≥5%时按预设金额补仓一次（替代止损，不受整体条件限制）',
    enabled: true,
    isPreset: true,
  },
  // 卖出条件
  {
    category: 'sell',
    ruleType: 'profit_threshold',
    operator: '>=',
    value: '5',
    params: { threshold: 5 },
    description: '盈利5%卖出',
    enabled: true,
    isPreset: true,
  },
  {
    category: 'sell',
    ruleType: 'loss_threshold',
    operator: '<=',
    value: '-5',
    params: { threshold: 5 },
    description: '亏损5%卖出（补仓规则启用时对未补仓持仓不触发）',
    enabled: true,
    isPreset: true,
  },
  {
    category: 'sell',
    ruleType: 'avg_down_sell',
    operator: '>=',
    value: '2.5',
    params: { pct: 2.5 },
    description: '补仓后较第二笔买价上涨≥2.5%时两笔全部卖出',
    enabled: true,
    isPreset: true,
  },
  {
    category: 'sell',
    ruleType: 'low_turnover_sell',
    value: '7',
    params: { days: 3, threshold: 7 },
    description: '连续3日换手率<7%时次日10点卖出',
    enabled: true,
    isPreset: true,
  },
  // 整体条件（2026-09-04：下跌方向只禁买入、可卖出；上涨方向两条已删除）
  {
    category: 'global',
    ruleType: 'market_breadth_pause',
    params: { count: 3500, direction: 'down', scope: 'buy' },
    description: '下跌个股>3500家时禁止买入（可卖出）',
    enabled: true,
    isPreset: true,
  },
  {
    category: 'global',
    ruleType: 'market_index_pause',
    params: { pct: 1, direction: 'down', scope: 'buy' },
    description: '大盘指数下跌1%时禁止买入（可卖出）',
    enabled: true,
    isPreset: true,
  },
  {
    category: 'global',
    ruleType: 'same_day_sell_block',
    description: '当日买入的股票当日不得卖出',
    enabled: true,
    isPreset: true,
  },
  // 执行参数（2026-09-06 新增）：从 settings 表迁到 conditions 表，可在条件管理页直接编辑
  {
    category: 'buy',
    ruleType: 'daily_max_buys',
    value: '25',
    params: { maxStocks: 25 },
    description: '每日最多买入只数（撞顶前按 buy_sort_by 排序截断）',
    enabled: true,
    isPreset: true,
  },
  {
    category: 'buy',
    ruleType: 'buy_sort_by',
    value: '3',
    params: { sortBy: 3 },
    description: '撞顶时优先选（1=换手率 2=振幅降序 3=综合分-推荐 4=字典序 5=换手×振幅 6-9=加权组合 10=振幅升序）',
    enabled: true,
    isPreset: true,
  },
  // 2026-09-16 新增：最近 N 个交易日至少 M 天上涨
  {
    category: 'buy',
    ruleType: 'screen_up_days',
    value: '2',
    params: { days: 3, minUpDays: 2 },
    description: '最近3个交易日至少2天上涨',
    enabled: true,
    isPreset: true,
  },
];

/** 导航菜单项 */
export const NAV_ITEMS = [
  { path: '/', label: '首页', icon: 'Home' },
  { path: '/today-brief', label: '今日简报', icon: 'Article' },
  { path: '/portfolio', label: '持仓', icon: 'AccountBalanceWallet' },
  { path: '/market', label: '行情', icon: 'TrendingUp' },
  { path: '/conditions', label: '条件', icon: 'Settings' },
  { path: '/history', label: '数据', icon: 'Download' },
  { path: '/summary', label: '总结', icon: 'Assessment' },
] as const;
